import { Injectable, inject } from '@angular/core';
import {
  AgencyKeys,
  Goal,
  GoalKeys,
  MgaSalesGoalCreateCommentRequest,
  MgaSalesGoalCommentGetListRequest,
  MgaSalesGoalCommentGetListResponse,
  MgaSalesGoalCommentListItemResponse,
  MgaSalesGoalPlaceholderDetailsResponse,
  MgaSalesGoalPlaceholderPayload,
  MgaSalesGoalPlaceholderResponse,
  PlaceholderSalesGoalsDetailsResponse,
  PlaceholderWithGoalsDetailsResponse,
  PlaceholderWithGoalsGetListResponse,
  PlaceholderWithGoalsListItemResponse,
  MgaSalesGoalUpdateCommentRequest,
  MgaSalesGoalsDetailsResponse,
  MgaSalesGoalsResponse,
  PlaceholderSalesGoalsResponse,
  MgaSalesGoalsUpsertRequest,
  MgaWithGoalsDetailsResponse,
  MgaWithGoalsGetListRequest,
  MgaWithGoalsGetListResponse,
  MgaWithGoalsKeys,
  MgaWithGoalsListItemResponse,
} from '@ag-common-lib/public-api';
import { LoadOptions } from 'devextreme/common/data';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { ReportingApiService } from './reporting-api.service';
import { normalizeGoalCommentsLoadOptions } from '../components/goal-comments/goal-comments-api.mapper';
import {
  applyMgaYearGoalSortRules,
  combineMgaSalesGoalsGridFilters,
  getMgaSalesGoalsDefaultSort,
  alignMgaSalesGoalsTotalSummary,
  getMgaSalesGoalsListSummary,
  getYearGoalField,
  MGA_PLACEHOLDER_ACTIVITY_ALL,
  MGA_SALES_GOALS_GRID_TYPE_FILTER,
  MGA_SALES_GOALS_GROUP_FIELD,
  MGA_SALES_GOALS_MGA_GROUP,
  MGA_SALES_GOALS_PLACEHOLDER_GROUP,
  MgaPlaceholderActivityFilter,
  MgaSalesGoalsGridRow,
  MgaSalesGoalsPlaceholder,
  omitMgaSalesGoalsCustomNameSummary,
  sumMgaSalesGoalsListSummaries,
  toMgaSalesGoalsSummaryNumber,
} from '../components/administration/mga-sales-goals-administration/mga-sales-goals.model';
import { mapPlaceholderToGridRow } from '../components/administration/mga-sales-goals-administration/mga-sales-goals-administration.mapper';

type SalesGoalIdentity = Pick<MgaSalesGoalsResponse | PlaceholderSalesGoalsResponse, 'id' | 'year' | 'amount'>;

const MGA_SALES_GOALS_GRID_PAGE_SIZE = 50;
const MGA_SALES_GOALS_GRID_FETCH_PAGE_SIZE = 400;
const MGA_SALES_GOALS_PLACEHOLDER_KEY_PREFIX = 'placeholder-';

interface MgaPlaceholderGoalRecord {
  id: number;
  year: number;
  amount: number;
}

interface MgaPlaceholderRecord {
  name: string;
  goals: MgaPlaceholderGoalRecord[];
  comments: MgaSalesGoalCommentListItemResponse[];
  nextId: number;
}

const mgaPlaceholderRecords = new Map<string, MgaPlaceholderRecord>();

interface MgaGridListCacheEntry {
  dataKey: string;
  hasSummary: boolean;
  response: MgaWithGoalsGetListResponse;
}

interface MgaGridListInflight {
  dataKey: string;
  hasSummary: boolean;
  requestId: number;
  promise: Promise<MgaWithGoalsGetListResponse>;
}

const isMgaPlaceholderKey = (agencyDbId: string): boolean =>
  agencyDbId.startsWith(MGA_SALES_GOALS_PLACEHOLDER_KEY_PREFIX);

const readPlaceholderId = (agencyDbId: string): number | null => {
  if (!isMgaPlaceholderKey(agencyDbId)) {
    return null;
  }

  const placeholderId = Number(agencyDbId.slice(MGA_SALES_GOALS_PLACEHOLDER_KEY_PREFIX.length));

  if (!Number.isInteger(placeholderId) || placeholderId <= 0) {
    return null;
  }

  return placeholderId;
};

const readMgaPlaceholderRecord = (agencyDbId: string): MgaPlaceholderRecord | null => {
  if (!isMgaPlaceholderKey(agencyDbId)) {
    return null;
  }

  const existing = mgaPlaceholderRecords.get(agencyDbId);

  if (existing) {
    return existing;
  }

  const created: MgaPlaceholderRecord = {
    name: '',
    goals: [],
    comments: [],
    nextId: 1,
  };

  mgaPlaceholderRecords.set(agencyDbId, created);

  return created;
};

const placeholderGoalsToGoals = (goals: MgaPlaceholderGoalRecord[]): Goal[] =>
  goals.map(goal => ({
    [GoalKeys.year]: goal.year,
    [GoalKeys.amount]: goal.amount,
  }));

const mapPlaceholderResponse = (placeholder: MgaSalesGoalPlaceholderResponse): MgaSalesGoalsPlaceholder => ({
  id: placeholder.id,
  key: `${MGA_SALES_GOALS_PLACEHOLDER_KEY_PREFIX}${placeholder.id}`,
  name: placeholder.name,
  goals: [],
});

const GRID_TO_API_FIELD_MAP: Record<string, string> = {
  [AgencyKeys.name]: MgaWithGoalsKeys.name,
  [AgencyKeys.agencyId]: MgaWithGoalsKeys.agencyPlatformId,
  [AgencyKeys.agencyType]: MgaWithGoalsKeys.type,
  key: MgaWithGoalsKeys.agencyPlatformId,
};

export type MgaSalesGoalsGridRowMapper = (
  items: MgaWithGoalsListItemResponse[],
  gridYears: number[],
) => MgaSalesGoalsGridRow[] | Promise<MgaSalesGoalsGridRow[]>;

@Injectable()
export class MgaSalesGoalsApiService {
  private readonly reportingApiService = inject(ReportingApiService);

  private readonly basePath = '/api/mga-sales-goals';
  private selectedYears: number[] = [];
  private placeholderActivityFilter: MgaPlaceholderActivityFilter = MGA_PLACEHOLDER_ACTIVITY_ALL;
  private agencyListSummary: Array<number | null> | undefined;
  private placeholderListSummary: Array<number | null> | undefined;
  private rowMapper: MgaSalesGoalsGridRowMapper | null = null;
  // The grid calls CustomStore.load again while virtual scrolling, summary, and grouping
  // settle, and later loads omit totalSummary once the footer is cached. The same rows
  // share one get-list until the query changes or the list is reloaded.
  private gridListRequestId = 0;
  private gridListCache: MgaGridListCacheEntry | null = null;
  private gridListInflight: MgaGridListInflight | null = null;
  private placeholderListRequestId = 0;
  private placeholderListCache: { dataKey: string; response: PlaceholderWithGoalsGetListResponse } | null = null;
  private placeholderListInflight: {
    dataKey: string;
    promise: Promise<PlaceholderWithGoalsGetListResponse>;
  } | null = null;

  private readonly gridDataSource = new DataSource({
    paginate: false,
    store: new CustomStore({
      key: 'key',
      load: async (loadOptions: LoadOptions) => {
        const [response, placeholderResponse] = await Promise.all([
          this.loadAllForGrid(loadOptions),
          this.loadAllPlaceholderGoals(loadOptions),
        ]);
        const mapRows = this.rowMapper;
        // A later grid load often omits totalSummary once the footer is cached. That response
        // has no summary array; keep the totals the footer is already showing.
        const agencySummary = getMgaSalesGoalsListSummary(response);
        const placeholderSummary = getMgaSalesGoalsListSummary(placeholderResponse);

        if (agencySummary !== undefined) {
          this.agencyListSummary = agencySummary;
        }

        if (placeholderSummary !== undefined) {
          this.placeholderListSummary = placeholderSummary;
        }
        const summary = alignMgaSalesGoalsTotalSummary(
          loadOptions.totalSummary,
          sumMgaSalesGoalsListSummaries(this.agencyListSummary, this.placeholderListSummary),
        );
        const agencyRows = mapRows
          ? await mapRows(response.data, this.selectedYears)
          : (response.data as unknown as MgaSalesGoalsGridRow[]);
        const placeholderRows = placeholderResponse.data.map(item => this.mapPlaceholderListItem(item));
        const data = [...agencyRows, ...placeholderRows];

        return {
          data,
          totalCount: data.length,
          ...(summary ? { summary } : {}),
        };
      },
    }),
  });

  registerPlaceholder(key: string, name: string): void {
    if (mgaPlaceholderRecords.has(key)) {
      return;
    }

    mgaPlaceholderRecords.set(key, {
      name,
      goals: [],
      comments: [],
      nextId: 1,
    });
  }

  createPlaceholder(name: string): Promise<MgaSalesGoalsPlaceholder> {
    return this.reportingApiService
      .request<MgaSalesGoalPlaceholderDetailsResponse>(`${this.basePath}/placeholder`, {
        method: 'POST',
        body: { name } satisfies MgaSalesGoalPlaceholderPayload,
      })
      .then(response => {
        const placeholder = mapPlaceholderResponse(response.data);

        this.registerPlaceholder(placeholder.key, placeholder.name);

        return placeholder;
      });
  }

  updatePlaceholder(placeholderId: number, name: string): Promise<MgaSalesGoalsPlaceholder> {
    return this.reportingApiService
      .request<MgaSalesGoalPlaceholderDetailsResponse>(`${this.basePath}/placeholder/${placeholderId}`, {
        method: 'PATCH',
        body: { name } satisfies MgaSalesGoalPlaceholderPayload,
      })
      .then(response => {
        const placeholder = mapPlaceholderResponse(response.data);
        const existing = mgaPlaceholderRecords.get(placeholder.key);

        if (existing) {
          existing.name = placeholder.name;
          return { ...placeholder, goals: placeholderGoalsToGoals(existing.goals) };
        }

        this.registerPlaceholder(placeholder.key, placeholder.name);

        return placeholder;
      });
  }

  deletePlaceholder(placeholderId: number): Promise<void> {
    return this.reportingApiService
      .request<void>(`${this.basePath}/placeholder/${placeholderId}`, {
        method: 'DELETE',
      })
      .then(() => {
        mgaPlaceholderRecords.delete(`${MGA_SALES_GOALS_PLACEHOLDER_KEY_PREFIX}${placeholderId}`);
      });
  }

  getPlaceholderName(agencyDbId: string): string {
    return readMgaPlaceholderRecord(agencyDbId)?.name ?? '';
  }

  setSelectedYears(years: number[]): void {
    this.selectedYears = [...years];
    this.invalidateGridListCache();
  }

  getGroupYearSummary(group: string | undefined, year: number, years: readonly number[]): number | null {
    const summary = this.summaryForGroup(group);

    if (!summary) {
      return null;
    }

    const index = years.indexOf(year);

    if (index < 0 || index >= summary.length) {
      return null;
    }

    return toMgaSalesGoalsSummaryNumber(summary[index]);
  }

  setPlaceholderActivityFilter(option: MgaPlaceholderActivityFilter): void {
    if (this.placeholderActivityFilter === option) {
      return;
    }

    this.placeholderActivityFilter = option;
    this.invalidateGridListCache();
  }

  setRowMapper(mapper: MgaSalesGoalsGridRowMapper | null): void {
    this.rowMapper = mapper;
  }

  getDataSource(): DataSource<MgaSalesGoalsGridRow, string> {
    return this.gridDataSource;
  }

  reloadDataSource(): Promise<void> {
    this.invalidateGridListCache();

    return this.gridDataSource.reload().then(() => undefined);
  }

  getList(
    loadOptions: LoadOptions | Record<string, unknown> = {},
    years: number[] = this.selectedYears,
  ): Promise<MgaWithGoalsGetListResponse> {
    return this.reportingApiService.request<MgaWithGoalsGetListResponse>(`${this.basePath}/agency/goals/get-list`, {
      method: 'POST',
      body: this.normalizeLoadOptions(loadOptions, years),
    });
  }

  getPlaceholderGoalsList(
    loadOptions: LoadOptions | Record<string, unknown> = {},
    years: number[] = this.selectedYears,
  ): Promise<PlaceholderWithGoalsGetListResponse> {
    return this.reportingApiService.request<PlaceholderWithGoalsGetListResponse>(
      `${this.basePath}/placeholder/goals/get-list`,
      {
        method: 'POST',
        body: this.normalizePlaceholderLoadOptions(loadOptions, years),
      },
    );
  }

  async getGoalsByAgencyDbId(agencyDbId: string): Promise<Goal[]> {
    const placeholderId = readPlaceholderId(agencyDbId);

    if (placeholderId) {
      const response = await this.reportingApiService.request<PlaceholderWithGoalsDetailsResponse>(
        this.placeholderGoalsPath(placeholderId),
      );

      return this.extractGoalsFromResponse(response.data);
    }

    if (!agencyDbId) {
      return [];
    }

    const response = await this.reportingApiService.request<MgaWithGoalsDetailsResponse>(
      this.agencyGoalsPath(agencyDbId),
    );

    return this.extractGoalsFromResponse(response.data);
  }

  /**
   * Goals and production for one agency in a single request. Production is aggregated
   * server-side from the same database the goals live in, so the caller does not fetch
   * transactions and join them itself.
   */
  async getGoalsAndProductionByAgencyDbId(
    agencyDbId: string,
    years: number[],
  ): Promise<{ goals: Goal[]; productionByYear: Map<number, number> }> {
    const placeholderId = readPlaceholderId(agencyDbId);

    if (placeholderId) {
      const goals = await this.getGoalsByAgencyDbId(agencyDbId);

      return { goals, productionByYear: new Map() };
    }

    const productionByYear = new Map<number, number>();
    if (!agencyDbId) {
      return { goals: [], productionByYear };
    }

    const query = years.map(year => `years=${year}`).join('&');
    const response = await this.reportingApiService.request<MgaWithGoalsDetailsResponse>(
      `${this.agencyGoalsPath(agencyDbId)}${query ? `?${query}` : ''}`,
    );

    for (const entry of response.data?.production ?? []) {
      productionByYear.set(entry.year, entry.premiumTotal ?? 0);
    }

    return { goals: this.extractGoalsFromResponse(response.data), productionByYear };
  }

  async upsertGoal(
    agencyDbId: string,
    payload: MgaSalesGoalsUpsertRequest,
    existingGoals: Goal[] = [],
  ): Promise<Goal[]> {
    const placeholder = readMgaPlaceholderRecord(agencyDbId);

    if (placeholder) {
      return this.upsertPlaceholderGoal(agencyDbId, placeholder, payload, existingGoals);
    }

    if (!agencyDbId) {
      return Promise.reject(new Error('Agency dbId is required'));
    }

    const response = await this.reportingApiService.request<MgaSalesGoalsDetailsResponse>(
      this.agencyGoalsPath(agencyDbId),
      {
        method: 'PUT',
        body: payload,
      },
    );

    const updatedGoal = this.mapUpsertResponseToGoal(response.data);

    return this.mergeGoalIntoGoals(existingGoals, updatedGoal);
  }

  async createComment(agencyDbId: string, payload: MgaSalesGoalCreateCommentRequest): Promise<void> {
    if (!agencyDbId) {
      return Promise.reject(new Error('Agency dbId is required'));
    }

    await this.reportingApiService.request<void>(`${this.basePath}/comments`, {
      method: 'POST',
      body: this.serializeCommentPayload(payload),
    });
  }

  async updateComment(agencyDbId: string, commentId: number, payload: MgaSalesGoalUpdateCommentRequest): Promise<void> {
    if (!agencyDbId) {
      return Promise.reject(new Error('Agency dbId is required'));
    }

    if (!commentId) {
      return Promise.reject(new Error('Comment id is required'));
    }

    await this.reportingApiService.request<void>(`${this.basePath}/comments/${commentId}`, {
      method: 'PUT',
      body: this.serializeCommentPayload(payload),
    });
  }

  async deleteComment(agencyDbId: string, commentId: number): Promise<void> {
    if (!agencyDbId) {
      return Promise.reject(new Error('Agency dbId is required'));
    }

    if (!commentId) {
      return Promise.reject(new Error('Comment id is required'));
    }

    await this.reportingApiService.request<void>(`${this.basePath}/comments/${commentId}`, {
      method: 'DELETE',
    });
  }

  async resolveGoalIdForYear(
    agencyDbId: string,
    year: number,
    existingGoals: Goal[] = [],
    actionName?: string,
  ): Promise<number> {
    const placeholder = readMgaPlaceholderRecord(agencyDbId);

    if (placeholder) {
      return this.resolvePlaceholderGoalId(agencyDbId, placeholder, year, existingGoals, actionName);
    }

    const mgaWithGoals = await this.getMgaWithGoalsDetails(agencyDbId);
    const goal = mgaWithGoals?.goals?.find(item => Number(item.year) === year) as SalesGoalIdentity | undefined;

    if (goal?.id) {
      return goal.id;
    }

    const amount =
      existingGoals.find(existingGoal => Number(existingGoal[GoalKeys.year]) === year)?.[GoalKeys.amount] ?? 0;

    const upsertResponse = await this.reportingApiService.request<MgaSalesGoalsDetailsResponse>(
      this.agencyGoalsPath(agencyDbId),
      {
        method: 'PUT',
        body: {
          year,
          amount,
          ...(actionName ? { actionName } : {}),
        } satisfies MgaSalesGoalsUpsertRequest,
      },
    );

    return upsertResponse.data.id;
  }

  getCommentsList(
    agencyDbId: string,
    loadOptions: LoadOptions | Record<string, unknown> = {},
  ): Promise<MgaSalesGoalCommentGetListResponse> {
    const placeholderId = readPlaceholderId(agencyDbId);
    const body = normalizeGoalCommentsLoadOptions(loadOptions) as MgaSalesGoalCommentGetListRequest;

    if (placeholderId) {
      return this.reportingApiService.request<MgaSalesGoalCommentGetListResponse>(
        `${this.basePath}/placeholder/${placeholderId}/comments/get-list`,
        {
          method: 'POST',
          body,
        },
      );
    }

    if (!agencyDbId) {
      return Promise.resolve({ data: [], totalCount: 0 });
    }

    return this.reportingApiService.request<MgaSalesGoalCommentGetListResponse>(
      `${this.basePath}/agency/${encodeURIComponent(agencyDbId)}/comments/get-list`,
      {
        method: 'POST',
        body,
      },
    );
  }

  private async upsertPlaceholderGoal(
    agencyDbId: string,
    placeholder: MgaPlaceholderRecord,
    payload: MgaSalesGoalsUpsertRequest,
    existingGoals: Goal[],
  ): Promise<Goal[]> {
    const placeholderId = readPlaceholderId(agencyDbId);

    if (!placeholderId) {
      return Promise.reject(new Error('Prospective MGAs id is required'));
    }

    const response = await this.reportingApiService.request<PlaceholderSalesGoalsDetailsResponse>(
      this.placeholderGoalsPath(placeholderId),
      {
        method: 'PUT',
        body: payload,
      },
    );

    this.rememberPlaceholderGoal(agencyDbId, placeholder, response.data);

    return this.mergeGoalIntoGoals(existingGoals, this.mapUpsertResponseToGoal(response.data));
  }

  private async resolvePlaceholderGoalId(
    agencyDbId: string,
    placeholder: MgaPlaceholderRecord,
    year: number,
    existingGoals: Goal[],
    actionName?: string,
  ): Promise<number> {
    const placeholderId = readPlaceholderId(agencyDbId);

    if (!placeholderId) {
      return Promise.reject(new Error('Prospective MGAs id is required'));
    }

    const details = await this.getPlaceholderWithGoalsDetails(placeholderId);
    const goal = details?.goals?.find(item => Number(item.year) === year) as SalesGoalIdentity | undefined;

    if (goal?.id) {
      this.rememberPlaceholderGoal(agencyDbId, placeholder, goal);
      return goal.id;
    }

    const amount = existingGoals.find(item => Number(item[GoalKeys.year]) === year)?.[GoalKeys.amount] ?? 0;
    const upsertResponse = await this.reportingApiService.request<PlaceholderSalesGoalsDetailsResponse>(
      this.placeholderGoalsPath(placeholderId),
      {
        method: 'PUT',
        body: {
          year,
          amount,
          ...(actionName ? { actionName } : {}),
        } satisfies MgaSalesGoalsUpsertRequest,
      },
    );

    this.rememberPlaceholderGoal(agencyDbId, placeholder, upsertResponse.data);

    return upsertResponse.data.id;
  }

  private rememberPlaceholderGoal(
    agencyDbId: string,
    placeholder: MgaPlaceholderRecord,
    goal: SalesGoalIdentity,
  ): void {
    const existing = placeholder.goals.find(item => item.year === goal.year);

    if (existing) {
      existing.id = goal.id;
      existing.amount = goal.amount;
      mgaPlaceholderRecords.set(agencyDbId, placeholder);
      return;
    }

    placeholder.goals.push({
      id: goal.id,
      year: goal.year,
      amount: goal.amount,
    });
    placeholder.nextId = Math.max(placeholder.nextId, goal.id + 1);
    mgaPlaceholderRecords.set(agencyDbId, placeholder);
  }

  private agencyGoalsPath(agencyDbId: string): string {
    return `${this.basePath}/agency/${encodeURIComponent(agencyDbId)}/goals`;
  }

  private placeholderGoalsPath(placeholderId: number): string {
    return `${this.basePath}/placeholder/${placeholderId}/goals`;
  }

  private async getPlaceholderWithGoalsDetails(
    placeholderId: number,
  ): Promise<PlaceholderWithGoalsDetailsResponse['data'] | null> {
    const response = await this.reportingApiService.request<PlaceholderWithGoalsDetailsResponse>(
      this.placeholderGoalsPath(placeholderId),
    );

    return response.data ?? null;
  }

  private serializeCommentPayload(
    payload: MgaSalesGoalCreateCommentRequest | MgaSalesGoalUpdateCommentRequest,
  ): Record<string, unknown> {
    const { commentedAt, ...rest } = payload;

    return {
      ...rest,
      ...(commentedAt !== undefined
        ? {
            commentedAt: commentedAt,
          }
        : {}),
    };
  }

  private async getMgaWithGoalsDetails(agencyDbId: string): Promise<MgaWithGoalsDetailsResponse['data'] | null> {
    if (!agencyDbId) {
      return null;
    }

    const response = await this.reportingApiService.request<MgaWithGoalsDetailsResponse>(
      this.agencyGoalsPath(agencyDbId),
    );

    return response.data ?? null;
  }

  private mapUpsertResponseToGoal(data: SalesGoalIdentity): Goal {
    return {
      [GoalKeys.year]: data.year,
      [GoalKeys.amount]: data.amount,
    };
  }

  private mergeGoalIntoGoals(existingGoals: Goal[], updatedGoal: Goal): Goal[] {
    const updatedYear = Number(updatedGoal[GoalKeys.year]);
    const remainingGoals = existingGoals.filter(goal => Number(goal[GoalKeys.year]) !== updatedYear);

    return [...remainingGoals, updatedGoal];
  }

  private extractGoalsFromResponse(data: Goal[] | Record<string, unknown> | null | undefined): Goal[] {
    if (Array.isArray(data)) {
      return this.normalizeGoals(data);
    }

    if (data && typeof data === 'object' && MgaWithGoalsKeys.goals in data) {
      return this.normalizeGoals((data as Record<string, Goal[]>)[MgaWithGoalsKeys.goals]);
    }

    return [];
  }

  private normalizeGoals(goals: Goal[] | null | undefined): Goal[] {
    if (!Array.isArray(goals)) {
      return [];
    }

    return goals.map(goal => ({
      [GoalKeys.year]: goal[GoalKeys.year],
      [GoalKeys.amount]: goal[GoalKeys.amount],
      ...(goal[GoalKeys.messages]?.length ? { [GoalKeys.messages]: goal[GoalKeys.messages] } : {}),
    }));
  }

  private loadAllForGrid(loadOptions: LoadOptions): Promise<MgaWithGoalsGetListResponse> {
    const request = this.gridListRequest(loadOptions);
    const dataKey = this.gridListDataKey(request);
    const asksForSummary = this.gridListRequestHasSummary(request);
    const cached = this.gridListCache;

    if (cached && this.canReuseGridList(cached, dataKey, asksForSummary)) {
      return Promise.resolve(cached.response);
    }

    const inflight = this.gridListInflight;

    if (inflight && this.canReuseGridList(inflight, dataKey, asksForSummary)) {
      return inflight.promise;
    }

    const requestId = ++this.gridListRequestId;
    const promise = this.fetchGridListPages(loadOptions).then(response => {
      this.rememberGridListResponse(requestId, dataKey, asksForSummary, response);

      return response;
    });

    void promise.finally(() => {
      if (this.gridListInflight?.promise === promise) {
        this.gridListInflight = null;
      }
    });

    this.gridListInflight = { dataKey, hasSummary: asksForSummary, requestId, promise };

    return promise;
  }

  private async fetchGridListPages(loadOptions: LoadOptions): Promise<MgaWithGoalsGetListResponse> {
    const pageSize = MGA_SALES_GOALS_GRID_FETCH_PAGE_SIZE;
    const first = await this.getList(this.toGridLoadOptions(loadOptions, 0, pageSize, true), this.selectedYears);
    const totalCount = first.totalCount ?? first.data.length;
    const data = [...first.data];

    for (let skip = data.length; skip < totalCount; skip += pageSize) {
      const page = await this.getList(this.toGridLoadOptions(loadOptions, skip, pageSize, false), this.selectedYears);

      if (!page.data.length) {
        break;
      }

      data.push(...page.data);
    }

    return { ...first, data, totalCount };
  }

  private loadAllPlaceholderGoals(loadOptions: LoadOptions): Promise<PlaceholderWithGoalsGetListResponse> {
    const request = this.placeholderListRequest(loadOptions);
    const dataKey = this.gridListDataKey(request);
    const cached = this.placeholderListCache;

    if (cached?.dataKey === dataKey) {
      return Promise.resolve(cached.response);
    }

    const inflight = this.placeholderListInflight;

    if (inflight?.dataKey === dataKey) {
      return inflight.promise;
    }

    const requestId = ++this.placeholderListRequestId;
    const promise = this.fetchPlaceholderGoalPages(loadOptions)
      .then(response => {
        if (this.placeholderListRequestId === requestId) {
          this.placeholderListCache = { dataKey, response };
        }

        return response;
      })
      .catch((error: unknown) => {
        console.error('Failed to load MGA sales goal for Prospective MGAs', error);

        return { data: [], totalCount: 0 };
      });

    void promise.finally(() => {
      if (this.placeholderListInflight?.promise === promise) {
        this.placeholderListInflight = null;
      }
    });

    this.placeholderListInflight = { dataKey, promise };

    return promise;
  }

  private async fetchPlaceholderGoalPages(loadOptions: LoadOptions): Promise<PlaceholderWithGoalsGetListResponse> {
    const pageSize = MGA_SALES_GOALS_GRID_FETCH_PAGE_SIZE;
    const first = await this.getPlaceholderGoalsList(
      this.toGridLoadOptions(loadOptions, 0, pageSize, true),
      this.selectedYears,
    );
    const totalCount = first.totalCount ?? first.data.length;
    const data = [...first.data];

    for (let skip = data.length; skip < totalCount; skip += pageSize) {
      const page = await this.getPlaceholderGoalsList(
        this.toGridLoadOptions(loadOptions, skip, pageSize, false),
        this.selectedYears,
      );

      if (!page.data.length) {
        break;
      }

      data.push(...page.data);
    }

    return { ...first, data, totalCount };
  }

  private placeholderListRequest(loadOptions: LoadOptions): Record<string, unknown> {
    return this.normalizePlaceholderLoadOptions(
      this.toGridLoadOptions(loadOptions, 0, MGA_SALES_GOALS_GRID_FETCH_PAGE_SIZE, true),
      this.selectedYears,
    ) as Record<string, unknown>;
  }

  // Prospective MGAs only. Agency goals get-list keeps normalizeLoadOptions unchanged.
  private normalizePlaceholderLoadOptions(
    loadOptions: LoadOptions | Record<string, unknown> | null | undefined,
    years: number[],
  ): MgaWithGoalsGetListRequest {
    const request = this.normalizeLoadOptions(loadOptions, years);

    return {
      ...request,
      placeholderActivity: this.placeholderActivityFilter,
    };
  }

  private mapPlaceholderListItem(item: PlaceholderWithGoalsListItemResponse): MgaSalesGoalsGridRow {
    const key = `${MGA_SALES_GOALS_PLACEHOLDER_KEY_PREFIX}${item.id}`;
    const apiGoals = this.goalsFromPlaceholderListItem(item);
    const existing = mgaPlaceholderRecords.get(key);

    if (!existing) {
      mgaPlaceholderRecords.set(key, {
        name: item.name,
        goals: apiGoals,
        comments: [],
        nextId: apiGoals.reduce((maxId, goal) => Math.max(maxId, goal.id), 0) + 1,
      });
    }

    if (existing) {
      existing.name = item.name;
      this.mergePlaceholderGoals(existing, apiGoals);
    }

    const record = mgaPlaceholderRecords.get(key);

    return mapPlaceholderToGridRow(
      {
        id: item.id,
        key,
        name: item.name,
        goals: placeholderGoalsToGoals(record?.goals ?? apiGoals),
      },
      this.selectedYears,
    );
  }

  private goalsFromPlaceholderListItem(item: PlaceholderWithGoalsListItemResponse): MgaPlaceholderGoalRecord[] {
    const goals: MgaPlaceholderGoalRecord[] = [];

    this.selectedYears.forEach(year => {
      const amount = this.readPlaceholderYearGoal(item, year);

      if (amount == null) {
        return;
      }

      goals.push({
        id: goals.length + 1,
        year,
        amount,
      });
    });

    return goals;
  }

  private readPlaceholderYearGoal(item: PlaceholderWithGoalsListItemResponse, year: number): number | null {
    const field = getYearGoalField(year);

    if (!(field in item)) {
      return null;
    }

    const amount = item[field as `year_${number}_goal`];

    return typeof amount === 'number' ? amount : null;
  }

  private mergePlaceholderGoals(record: MgaPlaceholderRecord, apiGoals: MgaPlaceholderGoalRecord[]): void {
    apiGoals.forEach(goal => {
      const alreadyStored = record.goals.some(stored => stored.year === goal.year);

      if (alreadyStored) {
        return;
      }

      record.goals.push({
        id: record.nextId,
        year: goal.year,
        amount: goal.amount,
      });
      record.nextId += 1;
    });
  }

  private summaryForGroup(group: string | undefined): Array<number | null> | undefined {
    if (group === MGA_SALES_GOALS_MGA_GROUP) {
      return this.agencyListSummary;
    }

    if (group === MGA_SALES_GOALS_PLACEHOLDER_GROUP) {
      return this.placeholderListSummary;
    }

    return undefined;
  }

  private invalidateGridListCache(): void {
    this.gridListRequestId += 1;
    this.gridListCache = null;
    this.gridListInflight = null;
    this.placeholderListRequestId += 1;
    this.placeholderListCache = null;
    this.placeholderListInflight = null;
  }

  private rememberGridListResponse(
    requestId: number,
    dataKey: string,
    hasSummary: boolean,
    response: MgaWithGoalsGetListResponse,
  ): void {
    if (this.gridListRequestId !== requestId) {
      return;
    }

    const current = this.gridListCache;

    if (current?.dataKey === dataKey && current.hasSummary && !hasSummary) {
      return;
    }

    this.gridListCache = { dataKey, hasSummary, response };
  }

  private canReuseGridList(
    entry: { dataKey: string; hasSummary: boolean },
    dataKey: string,
    asksForSummary: boolean,
  ): boolean {
    if (entry.dataKey !== dataKey) {
      return false;
    }

    if (asksForSummary && !entry.hasSummary) {
      return false;
    }

    return true;
  }

  private gridListRequest(loadOptions: LoadOptions): Record<string, unknown> {
    return this.normalizeLoadOptions(
      this.toGridLoadOptions(loadOptions, 0, MGA_SALES_GOALS_GRID_FETCH_PAGE_SIZE, true),
      this.selectedYears,
    ) as Record<string, unknown>;
  }

  private gridListDataKey(request: Record<string, unknown>): string {
    return JSON.stringify({
      filter: request['filter'] ?? null,
      sort: request['sort'] ?? null,
      searchExpr: request['searchExpr'] ?? null,
      searchOperation: request['searchOperation'] ?? null,
      searchValue: request['searchValue'] ?? null,
      years: request['years'] ?? null,
      placeholderActivity: request['placeholderActivity'] ?? null,
    });
  }

  private gridListRequestHasSummary(request: Record<string, unknown>): boolean {
    const totalSummary = request['totalSummary'];

    return Array.isArray(totalSummary) && totalSummary.length > 0;
  }

  private toGridLoadOptions(
    loadOptions: LoadOptions,
    skip: number,
    take: number,
    includeSummary: boolean,
  ): LoadOptions {
    const next: LoadOptions = { ...loadOptions, skip, take };

    delete next.group;
    delete next.requireGroupCount;
    next.sort = this.omitGroupSort(next.sort);

    if (!includeSummary) {
      delete next.totalSummary;
    }

    return next;
  }

  private omitGroupSort(sort: LoadOptions['sort']): LoadOptions['sort'] {
    if (!Array.isArray(sort)) {
      return this.isGroupSortItem(sort) ? undefined : sort;
    }

    const next = sort.filter(item => !this.isGroupSortItem(item));

    return next.length ? next : undefined;
  }

  private isGroupSortItem(item: unknown): boolean {
    if (item === MGA_SALES_GOALS_GROUP_FIELD) {
      return true;
    }

    if (!item || typeof item !== 'object' || !('selector' in item)) {
      return false;
    }

    return (item as { selector?: string }).selector === MGA_SALES_GOALS_GROUP_FIELD;
  }

  private normalizeLoadOptions(
    loadOptions: LoadOptions | Record<string, unknown> | null | undefined,
    years: number[],
  ): MgaWithGoalsGetListRequest {
    if (!loadOptions || typeof loadOptions !== 'object') {
      return {
        skip: 0,
        take: MGA_SALES_GOALS_GRID_PAGE_SIZE,
        requireTotalCount: true,
        sort: applyMgaYearGoalSortRules(getMgaSalesGoalsDefaultSort(MgaWithGoalsKeys.name)),
        filter: this.mapFilterSelectors([...MGA_SALES_GOALS_GRID_TYPE_FILTER]),
        years,
      };
    }

    const normalizedLoadOptions = Object.entries(loadOptions as Record<string, unknown>).reduce<
      Record<string, unknown>
    >((result, [key, value]) => {
      if (value === undefined) {
        return result;
      }

      result[key] = value;
      return result;
    }, {});

    normalizedLoadOptions.requireTotalCount = true;
    normalizedLoadOptions['skip'] ??= 0;
    normalizedLoadOptions['take'] ??= MGA_SALES_GOALS_GRID_PAGE_SIZE;

    if (normalizedLoadOptions['sort']) {
      normalizedLoadOptions['sort'] = applyMgaYearGoalSortRules(this.mapSelectorItems(normalizedLoadOptions['sort']));
    }

    if (!normalizedLoadOptions['sort']) {
      normalizedLoadOptions['sort'] = applyMgaYearGoalSortRules(getMgaSalesGoalsDefaultSort(MgaWithGoalsKeys.name));
    }

    if (normalizedLoadOptions['totalSummary']) {
      normalizedLoadOptions['totalSummary'] = omitMgaSalesGoalsCustomNameSummary(
        this.mapSelectorItems(normalizedLoadOptions['totalSummary']),
      );
    }

    const combinedFilter = combineMgaSalesGoalsGridFilters(normalizedLoadOptions['filter'], [
      ...MGA_SALES_GOALS_GRID_TYPE_FILTER,
    ]);

    if (combinedFilter) {
      normalizedLoadOptions['filter'] = this.mapFilterSelectors(combinedFilter);
    }

    return {
      ...(normalizedLoadOptions as LoadOptions),
      years,
    };
  }

  private mapSelectorItems(items: unknown): unknown {
    if (!Array.isArray(items)) {
      return items;
    }

    return items.map(item => {
      if (!item || typeof item !== 'object') {
        return item;
      }

      const selectorItem = item as { selector?: string };

      if (!selectorItem.selector) {
        return item;
      }

      return {
        ...selectorItem,
        selector: this.mapGridFieldToApiField(selectorItem.selector),
      };
    });
  }

  private mapFilterSelectors(filter: unknown): unknown {
    if (!Array.isArray(filter)) {
      return filter;
    }

    if (filter.length === 3 && typeof filter[0] === 'string') {
      return [this.mapGridFieldToApiField(filter[0]), filter[1], filter[2]];
    }

    return filter.map(item => {
      if (item === 'and' || item === 'or') {
        return item;
      }

      return this.mapFilterSelectors(item);
    });
  }

  private mapGridFieldToApiField(selector: string): string {
    if (GRID_TO_API_FIELD_MAP[selector]) {
      return GRID_TO_API_FIELD_MAP[selector];
    }

    if (/^year_\d+_goal$/.test(selector)) {
      return selector;
    }

    return selector;
  }
}
